import type {
  MarketDefinitionT,
  TradeWorkbenchDenominationT,
  TradeWorkbenchSidebarTabT,
  TradeWorkbenchTimezoneT,
} from '@web/routes/trade/-workstation/types';
import {
  isTradeWorkbenchSidebarTab,
  isTradeWorkbenchTimezone,
} from '@web/routes/trade/-workstation/types';

export const TRADE_WORKBENCH_PREFERENCES_STORAGE_KEY =
  'hypersense.trade-workbench-preferences';

type StoredTradeWorkbenchPreferencesT = {
  denomination: TradeWorkbenchDenominationT;
  orderBookGroupingsByAsset: Record<string, unknown>;
  sidebarTab: TradeWorkbenchSidebarTabT;
  timezone: TradeWorkbenchTimezoneT;
};

export type TradeWorkbenchPreferencesT = {
  denomination: TradeWorkbenchDenominationT;
  orderBookGrouping: number;
  sidebarTab: TradeWorkbenchSidebarTabT;
  timezone: TradeWorkbenchTimezoneT;
};

export function readTradeWorkbenchPreferences(input: {
  market: MarketDefinitionT;
  storageValue: null | string;
}): TradeWorkbenchPreferencesT {
  const storedPreferences = readStoredTradeWorkbenchPreferences({
    storageValue: input.storageValue,
  });

  return {
    denomination: readStoredDenomination(storedPreferences),
    orderBookGrouping: readStoredOrderBookGrouping({
      market: input.market,
      storedPreferences,
    }),
    sidebarTab: readStoredSidebarTab(storedPreferences),
    timezone: readStoredTimezone(storedPreferences),
  };
}

export function buildTradeWorkbenchPreferencesStorageValue(input: {
  asset: string;
  currentStorageValue: null | string;
  preferences: TradeWorkbenchPreferencesT;
}): string {
  const currentStoredPreferences = readStoredTradeWorkbenchPreferences({
    storageValue: input.currentStorageValue,
  });
  const storedOrderBookGroupingsByAsset = readStoredOrderBookGroupingsByAsset(
    currentStoredPreferences,
  );

  return JSON.stringify({
    denomination: input.preferences.denomination,
    orderBookGroupingsByAsset: {
      ...storedOrderBookGroupingsByAsset,
      [input.asset]: input.preferences.orderBookGrouping,
    },
    sidebarTab: input.preferences.sidebarTab,
    timezone: input.preferences.timezone,
  } satisfies StoredTradeWorkbenchPreferencesT);
}

function readStoredTradeWorkbenchPreferences(input: {
  storageValue: null | string;
}) {
  if (input.storageValue === null) {
    return null;
  }

  try {
    const parsedValue: unknown = JSON.parse(input.storageValue);

    if (!isRecord(parsedValue)) {
      return null;
    }

    return {
      denomination: readStoredDenomination(parsedValue),
      orderBookGroupingsByAsset:
        readStoredOrderBookGroupingsByAsset(parsedValue),
      sidebarTab: readStoredSidebarTab(parsedValue),
      timezone: readStoredTimezone(parsedValue),
    } satisfies StoredTradeWorkbenchPreferencesT;
  } catch {
    return null;
  }
}

function readStoredDenomination(
  storedPreferences:
    | null
    | StoredTradeWorkbenchPreferencesT
    | Record<string, unknown>,
): TradeWorkbenchDenominationT {
  const storedDenomination = storedPreferences?.denomination;

  return storedDenomination === 'usdc' ? 'usdc' : 'native';
}

function readStoredOrderBookGrouping(input: {
  market: MarketDefinitionT;
  storedPreferences: null | StoredTradeWorkbenchPreferencesT;
}) {
  const storedOrderBookGrouping = readStoredOrderBookGroupingsByAsset(
    input.storedPreferences,
  )[input.market.asset];

  if (
    typeof storedOrderBookGrouping === 'number' &&
    input.market.orderBookGroupings.includes(storedOrderBookGrouping)
  ) {
    return storedOrderBookGrouping;
  }

  return input.market.defaultOrderBookGrouping;
}

function readStoredOrderBookGroupingsByAsset(
  storedPreferences:
    | null
    | StoredTradeWorkbenchPreferencesT
    | Record<string, unknown>,
) {
  const storedOrderBookGroupingsByAsset =
    storedPreferences?.orderBookGroupingsByAsset;

  return isRecord(storedOrderBookGroupingsByAsset)
    ? storedOrderBookGroupingsByAsset
    : {};
}

function readStoredSidebarTab(
  storedPreferences:
    | null
    | StoredTradeWorkbenchPreferencesT
    | Record<string, unknown>,
) {
  if (!isTradeWorkbenchSidebarTab(storedPreferences?.sidebarTab)) {
    return 'order-book';
  }

  return storedPreferences.sidebarTab;
}

function readStoredTimezone(
  storedPreferences:
    | null
    | StoredTradeWorkbenchPreferencesT
    | Record<string, unknown>,
) {
  if (!isTradeWorkbenchTimezone(storedPreferences?.timezone)) {
    return 'local';
  }

  return storedPreferences.timezone;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}
